import React from "react";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Slider,
  TextField,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tooltip,
  Button,
  FormGroup,
  Checkbox,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import TuneIcon from "@mui/icons-material/Tune";
import ShieldIcon from "@mui/icons-material/Shield";
import PublicIcon from "@mui/icons-material/Public";
import IndicatorStackPanel from "./IndicatorStackPanel";
import { MARKET_SESSIONS } from "../widgets/MarketSessionsMonitor";

/**
 * Level 3: Advanced Accordions
 * Detailed settings organized into collapsible sections.
 * Each accordion is independently expandable.
 */
export default function AdvancedAccordions({
  settings,
  setSettings,
  handleSettingChange,
}) {
  const isBoolTrue = (val) => val === "Yes" || val === true;
  const isBoolOn = (val) => val !== false && val !== "No";

  // ── Budget Planner helper: compute preview values ─────────────────────
  const plannerOn = isBoolTrue(settings.strategyBudgetPlannerEnabled);
  const budgetPreview = React.useMemo(() => {
    if (!plannerOn) return null;
    const B = parseFloat(settings.strategyBudgetUsd) || 1000;
    const N = parseInt(settings.maxConcurrentPositions) || 5;
    const bufPct = (parseFloat(settings.budgetBufferPct) || 10) / 100;
    const L = (settings.tradingMode === 'margin' || settings.tradingMode === 'futures')
      ? Math.max(1, parseFloat(settings.leverage) || 1) : 1;
    const rescueOn = isBoolTrue(settings.rescueScaleInEnabled);
    const R = rescueOn ? (parseInt(settings.maxRescueAdds) || 1) : 0;
    const P = (parseFloat(settings.rescueAddPremiumPct) || 10) / 100;
    const maxExpMult = parseFloat(settings.maxExposureMult) || 3.0;
    const maxAddPct = (parseFloat(settings.maxRescueAddPctOfSlot) || 60) / 100;

    const B_avail = B * (1 - bufPct);
    const slotBudget = B_avail / N;
    const feeReserve = slotBudget * 0.03;
    const slipReserve = slotBudget * 0.02;
    const M_pool = slotBudget - feeReserve - slipReserve;
    if (M_pool <= 0) return { feasible: false, B_avail, slotBudget, M0: 0, entryNotional: 0, rEffective: 0, worstNotional: 0, L };

    // Binary search for M0
    let lo = 1 / L, hi = M_pool, bestM0 = lo;
    for (let i = 0; i < 40; i++) {
      const mid = (lo + hi) / 2;
      let tM = mid, tN = mid * L;
      const baseN = tN;
      let fits = true;
      for (let k = 0; k < R; k++) {
        let addN = tN * (1 + P);
        const remaining = baseN * maxExpMult - tN;
        if (remaining <= 0) break;
        if (addN > remaining) addN = remaining;
        const addM = addN / L;
        if (addM > maxAddPct * slotBudget) addN = maxAddPct * slotBudget * L;
        if (addN < 5) break;
        tM += addN / L;
        tN += addN;
      }
      if (tM <= M_pool) { bestM0 = mid; lo = mid; } else { hi = mid; }
    }
    // Recount effective rescues
    let totalM = bestM0, totalN = bestM0 * L, rEff = 0;
    const baseN2 = totalN;
    for (let k = 0; k < R; k++) {
      let addN = totalN * (1 + P);
      const rem = baseN2 * maxExpMult - totalN;
      if (rem <= 0) break;
      if (addN > rem) addN = rem;
      if (addN / L > maxAddPct * slotBudget) addN = maxAddPct * slotBudget * L;
      if (addN < 5) break;
      totalM += addN / L;
      totalN += addN;
      rEff++;
    }
    return {
      feasible: bestM0 * L >= 5 && totalM <= M_pool,
      B_avail, slotBudget, M0: bestM0, entryNotional: bestM0 * L,
      rEffective: rEff, worstNotional: totalN, L,
      feeReserve, slipReserve,
    };
  }, [plannerOn, settings.strategyBudgetUsd, settings.maxConcurrentPositions,
      settings.budgetBufferPct, settings.tradingMode, settings.leverage,
      settings.rescueScaleInEnabled, settings.maxRescueAdds, settings.rescueAddPremiumPct,
      settings.maxExposureMult, settings.maxRescueAddPctOfSlot]);

  // ── Decision Profile definitions (mirrors backend decision-profiles.ts) ──
  const DECISION_PROFILES = [
    { id: 'fast_green_scalp_5x', name: 'Fast Green Scalp 5×', description: 'Quick in/out trades targeting 1-3% net profit. Strict direction + loose micro-gates.', color: '#4caf50' },
    { id: 'trend_continuation_5x', name: 'Trend Continuation 5×', description: 'Wait for confirmed trend then ride. Fewer entries, higher quality.', color: '#2196f3' },
    { id: 'range_scalper_5x', name: 'Range Scalper 5×', description: 'Trade within ranges. Buy near support, sell near resistance.', color: '#ff9800' },
    { id: 'breakout_expansion_5x', name: 'Breakout Expansion 5×', description: 'Only enter on strong breakouts with high momentum.', color: '#9c27b0' },
    { id: 'conservative_recovery', name: 'Conservative Recovery', description: 'For drawdown periods. Very strict entry, tight stops.', color: '#f44336' },
  ];

  // ── Section: Decision Model ─────────────────────────────────────────────
  const decisionModelSection = (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SmartToyIcon color="primary" fontSize="small" />
          <Tooltip title="Select a decision model that auto-configures gate levels (P0/P1/P2) and exit settings for optimal results." arrow>
            <Typography variant="subtitle2" fontWeight="bold">Decision Model</Typography>
          </Tooltip>
          <Chip
            label={DECISION_PROFILES.find(p => p.id === settings.decisionProfile)?.name || 'Not Set'}
            size="small"
            variant="outlined"
            color={settings.decisionProfile ? "primary" : "default"}
            sx={{ fontSize: "0.65rem", height: 20 }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Decision Profile</InputLabel>
              <Select
                value={settings.decisionProfile || ''}
                label="Decision Profile"
                onChange={(e) => handleSettingChange("decisionProfile", e.target.value || undefined)}
              >
                <MenuItem value="">
                  <em>None (Legacy behavior)</em>
                </MenuItem>
                {DECISION_PROFILES.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color }} />
                      {p.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {settings.decisionProfile && (
            <>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  {DECISION_PROFILES.find(p => p.id === settings.decisionProfile)?.description}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Gate Classification: <strong>P0</strong> = Hard Rail (never bypassed) · <strong>P1</strong> = Primary Gate (blocks entry) · <strong>P2</strong> = Soft Score (weighted, never blocks alone)
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={settings.directionStackEnabled !== false}
                      onChange={(e) => handleSettingChange("directionStackEnabled", e.target.checked)}
                      color="primary"
                    />
                  }
                  label={<Typography variant="body2">Direction Stack (P0)</Typography>}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={settings.autoCloseAtTP1 === true}
                      onChange={(e) => handleSettingChange("autoCloseAtTP1", e.target.checked)}
                      color="success"
                    />
                  }
                  label={<Typography variant="body2">Auto-Close at TP1</Typography>}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={settings.velocityCrashEnabled !== false}
                      onChange={(e) => handleSettingChange("velocityCrashEnabled", e.target.checked)}
                      color="warning"
                    />
                  }
                  label={<Typography variant="body2">Velocity Crash Kill-Switch</Typography>}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Hard Loss Cap (net %)"
                  type="number"
                  size="small"
                  fullWidth
                  value={settings.hardLossCapNet ?? -1.3}
                  onChange={(e) => handleSettingChange("hardLossCapNet", parseFloat(e.target.value))}
                  inputProps={{ step: 0.1, min: -10, max: 0 }}
                  helperText="Max loss per trade (net)"
                />
              </Grid>
            </>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  // ── Section: Money Management ──────────────────────────────────────────
  const moneyManagementSection = (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <MonetizationOnIcon color="success" fontSize="small" />
          <Tooltip title="Configure how much capital to allocate per trade, maximum concurrent positions, and risk limits per symbol." arrow>
            <Typography variant="subtitle2" fontWeight="bold">Money Management</Typography>
          </Tooltip>
          <Chip
            label={plannerOn ? `Budget $${settings.strategyBudgetUsd || "1000"} ÷ ${settings.maxConcurrentPositions || "5"}` : `$${settings.positionSizeUsd || "100"} × ${settings.maxConcurrentPositions || "5"}`}
            size="small"
            variant="outlined"
            color={plannerOn ? "primary" : "default"}
            sx={{ fontSize: "0.65rem", height: 20 }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Strategy Budget Planner Toggle */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={plannerOn}
                  onChange={(e) => handleSettingChange("strategyBudgetPlannerEnabled", e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Tooltip title="When ON, the system dynamically computes position sizing from your strategy budget, accounting for rescue reserves, fees, and leverage. Disables manual Position Size ($)." arrow>
                  <Typography variant="body2" fontWeight="bold">Use Strategy Budget Planner</Typography>
                </Tooltip>
              }
            />
          </Grid>

          {/* Budget Planner Fields (visible when ON) */}
          {plannerOn && (
            <>
              {/* Budget Source Mode — controls where the budget comes from */}
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Budget Source</InputLabel>
                  <Select
                    value={settings.budgetSourceMode || "MANUAL_FIXED_BUDGET"}
                    label="Budget Source"
                    onChange={(e) => handleSettingChange("budgetSourceMode", e.target.value)}
                  >
                    <MenuItem value="MANUAL_FIXED_BUDGET">Manual (fixed amount)</MenuItem>
                    <MenuItem value="API_AVAILABLE_BALANCE">Live Broker Balance</MenuItem>
                    <MenuItem value="API_BUT_CAPPED">Live Balance (capped)</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {settings.budgetSourceMode === "API_AVAILABLE_BALANCE" ? "Reads actual USDT balance from broker API" :
                     settings.budgetSourceMode === "API_BUT_CAPPED" ? "Reads broker balance, capped at Budget Cap" :
                     "Uses fixed Strategy Budget amount below"}
                  </Typography>
                </FormControl>
              </Grid>
              {settings.budgetSourceMode === "API_BUT_CAPPED" && (
                <Grid item xs={6}>
                  <TextField fullWidth size="small" type="number" label="Budget Cap (USDT)"
                    value={settings.budgetCapUsd || ""}
                    onChange={(e) => handleSettingChange("budgetCapUsd", e.target.value)}
                    inputProps={{ min: 50, step: 100 }}
                    helperText="Max budget even if broker has more"
                  />
                </Grid>
              )}
              {(settings.budgetSourceMode === "API_AVAILABLE_BALANCE" || settings.budgetSourceMode === "API_BUT_CAPPED") && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    <Typography variant="caption">
                      <strong>Live Budget Sync:</strong> Reads your broker balance every 15s. Blocks entries if balance data is stale (&gt;60s). Accounts for open orders, rescue reserves, fees, and leverage margin across all running strategies.
                    </Typography>
                  </Alert>
                </Grid>
              )}
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small"
                  label={settings.budgetSourceMode === "MANUAL_FIXED_BUDGET" || !settings.budgetSourceMode ? "Strategy Budget (USDT)" : "Fallback Budget (USDT)"}
                  type="number"
                  value={settings.strategyBudgetUsd || "1000"}
                  onChange={(e) => handleSettingChange("strategyBudgetUsd", e.target.value)}
                  inputProps={{ min: 50, step: 100 }}
                  helperText={settings.budgetSourceMode === "MANUAL_FIXED_BUDGET" || !settings.budgetSourceMode ? "Total capital for this strategy" : "Used if API sync fails"}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small"
                  label="Max Concurrent Positions (N)"
                  type="number"
                  value={settings.maxConcurrentPositions || "5"}
                  onChange={(e) => handleSettingChange("maxConcurrentPositions", e.target.value)}
                  inputProps={{ min: 1, max: 100 }}
                  helperText="Slots — budget split equally"
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" gutterBottom>
                  Reserve Buffer: {settings.budgetBufferPct || 10}%
                </Typography>
                <Slider
                  value={parseInt(settings.budgetBufferPct) || 10}
                  onChange={(e, value) => handleSettingChange("budgetBufferPct", value.toString())}
                  min={0} max={25} step={1}
                  marks={[{ value: 0, label: '0%' }, { value: 10, label: '10%' }, { value: 25, label: '25%' }]}
                  valueLabelDisplay="auto"
                  size="small"
                />
                <Typography variant="caption" color="text.secondary">
                  Safety margin for slippage, funding, rounding
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Fee Model</InputLabel>
                  <Select
                    value={settings.feeModel || "auto"}
                    label="Fee Model"
                    onChange={(e) => handleSettingChange("feeModel", e.target.value)}
                  >
                    <MenuItem value="auto">Auto (0.16% RT notional)</MenuItem>
                    <MenuItem value="manual">Manual</MenuItem>
                  </Select>
                </FormControl>
                {settings.feeModel === 'manual' && (
                  <TextField
                    fullWidth size="small" type="number" label="RT Fee % (Margin)"
                    value={settings.feeRtPctMargin || "0.60"}
                    onChange={(e) => handleSettingChange("feeRtPctMargin", e.target.value)}
                    inputProps={{ step: 0.05, min: 0 }}
                    sx={{ mt: 1 }}
                  />
                )}
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="caption" color="text.secondary" fontWeight="bold">Safety Rails</Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" type="number"
                  label="Max Exposure Mult"
                  value={settings.maxExposureMult || "3.0"}
                  onChange={(e) => handleSettingChange("maxExposureMult", e.target.value)}
                  inputProps={{ step: 0.5, min: 1.5, max: 10 }}
                  helperText="Max total notional ÷ base entry"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" type="number"
                  label="Max Rescue Add % of Slot"
                  value={settings.maxRescueAddPctOfSlot || "60"}
                  onChange={(e) => handleSettingChange("maxRescueAddPctOfSlot", e.target.value)}
                  inputProps={{ step: 5, min: 10, max: 100 }}
                  helperText="Cap each rescue add margin"
                />
              </Grid>

              {/* Computed Preview */}
              {budgetPreview && (
                <Grid item xs={12}>
                  <Alert severity={budgetPreview.feasible ? "success" : "warning"} sx={{ py: 0.5 }}>
                    <Typography variant="caption" component="div">
                      <strong>Budget Plan Preview</strong>
                      {budgetPreview.L > 1 && <> ({budgetPreview.L}× leverage)</>}
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                      Tradable: <strong>${budgetPreview.B_avail?.toFixed(2)}</strong> |
                      Slot: <strong>${budgetPreview.slotBudget?.toFixed(2)}</strong> |
                      Entry: <strong>${budgetPreview.entryNotional?.toFixed(2)}</strong> notional
                      {budgetPreview.L > 1 && <> (${budgetPreview.M0?.toFixed(2)} margin)</>}
                    </Typography>
                    {budgetPreview.rEffective > 0 && (
                      <Typography variant="caption" component="div">
                        Rescues: <strong>{budgetPreview.rEffective}</strong> adds |
                        Worst-case: <strong>${budgetPreview.worstNotional?.toFixed(2)}</strong> notional
                      </Typography>
                    )}
                    {!budgetPreview.feasible && (
                      <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5 }}>
                        Budget too small — increase budget or reduce concurrent positions / rescue adds.
                      </Typography>
                    )}
                  </Alert>
                </Grid>
              )}
            </>
          )}

          {/* Position Size — disabled when planner is ON */}
          <Grid item xs={6}>
            <TextField
              fullWidth size="small"
              label="Position Size ($)"
              type="number"
              value={plannerOn && budgetPreview ? budgetPreview.entryNotional?.toFixed(2) : (settings.positionSizeUsd || "100")}
              onChange={(e) => handleSettingChange("positionSizeUsd", e.target.value)}
              disabled={plannerOn}
              helperText={plannerOn ? "Auto-computed by Budget Planner" : "USD allocated per new trade entry"}
            />
          </Grid>
          {!plannerOn && (
            <Grid item xs={6}>
              <TextField
                fullWidth size="small"
                label="Max Concurrent Positions"
                type="number"
                value={settings.maxConcurrentPositions || "5"}
                onChange={(e) => handleSettingChange("maxConcurrentPositions", e.target.value)}
                inputProps={{ min: 1, max: 100 }}
                helperText="Max open positions at any time"
              />
            </Grid>
          )}
          {!plannerOn && (
            <Grid item xs={6}>
              <Typography variant="body2" gutterBottom>
                Reserve: {(isBoolTrue(settings.rescueScaleInEnabled)) ? "N/A (rescue)" : `${settings.reservePercent || 15}%`}
              </Typography>
              <Slider
                value={parseInt(settings.reservePercent) || 15}
                onChange={(e, value) => handleSettingChange("reservePercent", value.toString())}
                min={0} max={50} step={5}
                disabled={isBoolTrue(settings.rescueScaleInEnabled)}
                size="small"
              />
            </Grid>
          )}
          {!plannerOn && (
            <Grid item xs={6}>
              <Alert severity="info" sx={{ py: 0.25, height: "100%", display: "flex", alignItems: "center" }}>
                <Typography variant="caption">
                  <strong>Est. capital:</strong>{" "}
                  ${(parseFloat(settings.positionSizeUsd || "100") * parseInt(settings.maxConcurrentPositions || "5")).toLocaleString()}
                </Typography>
              </Alert>
            </Grid>
          )}
          <Grid item xs={4}>
            <TextField fullWidth size="small" type="number" label="Max % / Trade"
              value={settings.maxPerTrade || "3"}
              onChange={(e) => handleSettingChange("maxPerTrade", e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" type="number" label="Max % / Symbol"
              value={settings.maxPerSymbol || "5"}
              onChange={(e) => handleSettingChange("maxPerSymbol", e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" type="number" label="Min Notional ($)"
              value={settings.minTradeNotional || "10"}
              onChange={(e) => handleSettingChange("minTradeNotional", e.target.value)}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  // ── Section: Entry Filters ─────────────────────────────────────────────
  const entryFiltersSection = (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SecurityIcon color="info" fontSize="small" />
          <Tooltip title="Gate conditions that must be met before a new trade can be opened. Helps filter out weak or risky signals." arrow>
            <Typography variant="subtitle2" fontWeight="bold">Entry Filters</Typography>
          </Tooltip>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolTrue(settings.strictEntryEnabled)} onChange={(e) => handleSettingChange("strictEntryEnabled", e.target.checked)} />}
              label={<Tooltip title="Requires multiple technical indicators to confirm entry direction before opening a trade." arrow><Typography variant="body2">Strict Entry Confirmation</Typography></Tooltip>}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolOn(settings.enableVolumeControl)} onChange={(e) => handleSettingChange("enableVolumeControl", e.target.checked)} />}
              label={<Tooltip title="Blocks entries on symbols with insufficient trading volume. Prevents trading illiquid assets." arrow><Typography variant="body2">Volume Control Gate</Typography></Tooltip>}
            />
          </Grid>
          {isBoolOn(settings.enableVolumeControl) && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Volume Strictness</InputLabel>
                <Select value={settings.volumeStrictness || "medium"} onChange={(e) => handleSettingChange("volumeStrictness", e.target.value)} label="Volume Strictness">
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolOn(settings.enableCorrelation)} onChange={(e) => handleSettingChange("enableCorrelation", e.target.checked)} />}
              label={<Tooltip title="Checks correlation between assets to avoid opening similar positions that move together, reducing portfolio risk." arrow><Typography variant="body2">Cross-Asset Correlation</Typography></Tooltip>}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolTrue(settings.sellFilterStrict)} onChange={(e) => handleSettingChange("sellFilterStrict", e.target.checked)} />}
              label={<Tooltip title="Applies stricter confirmation for short/sell entries. Higher confidence boost required at higher leverage." arrow><Typography variant="body2">Sell Filter Strict</Typography></Tooltip>}
            />
          </Grid>
          {/* Multi-Timeframe Trend Filters */}
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Tooltip title="Only allows entries that align with the trend direction on selected timeframes. Each enabled timeframe independently blocks counter-trend trades." arrow>
              <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 0.5 }}>
                Trend Direction Filters
              </Typography>
            </Tooltip>
          </Grid>
          {[
            { key: 'trendFilter1m',  label: '1m Trend',  tip: 'Block entries against 1-minute trend (very fast, noisy — use with caution).' },
            { key: 'trendFilter3m',  label: '3m Trend',  tip: 'Block entries against 3-minute trend. Good for scalp confirmation.' },
            { key: 'trendFilter5m',  label: '5m Trend',  tip: 'Block entries against 5-minute trend. Filters intra-candle noise.' },
            { key: 'trendFilter15m', label: '15m Trend', tip: 'Block entries against 15-minute trend. Balanced noise/signal filter.' },
            { key: 'trendFilter30m', label: '30m Trend', tip: 'Block entries against 30-minute trend. Classic swing filter.', defaultOn: true },
            { key: 'trendFilter1h',  label: '1h Trend',  tip: 'Block entries against 1-hour trend. Strong higher-timeframe filter.' },
            { key: 'trendFilter4h',  label: '4h Trend',  tip: 'Block entries against 4-hour trend. Strongest macro-trend filter.' },
          ].map((tf) => (
            <Grid item xs={6} sm={4} md={3} key={tf.key}>
              <FormControlLabel
                control={<Switch size="small" checked={tf.defaultOn ? isBoolOn(settings[tf.key]) : isBoolTrue(settings[tf.key])} onChange={(e) => handleSettingChange(tf.key, e.target.checked)} />}
                label={<Tooltip title={tf.tip} arrow><Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{tf.label}</Typography></Tooltip>}
              />
            </Grid>
          ))}
          {/* v4.4.11: Last Candle Confirmation Filter */}
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Tooltip title="Requires the last closed candle on selected timeframes to match the signal direction (green candle for BUY, red candle for SELL). Acts as final confirmation before entry." arrow>
              <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 0.5 }}>
                Last Candle Confirmation
              </Typography>
            </Tooltip>
          </Grid>
          {[
            { key: 'lastCandleFilter1m',  label: '1m Candle',  tip: 'Require last 1-minute candle to match signal direction (green=buy, red=sell).' },
            { key: 'lastCandleFilter3m',  label: '3m Candle',  tip: 'Require last 3-minute candle to match signal direction.' },
            { key: 'lastCandleFilter5m',  label: '5m Candle',  tip: 'Require last 5-minute candle to match signal direction.' },
            { key: 'lastCandleFilter15m', label: '15m Candle', tip: 'Require last 15-minute candle to match signal direction.' },
            { key: 'lastCandleFilter30m', label: '30m Candle', tip: 'Require last 30-minute candle to match signal direction.' },
            { key: 'lastCandleFilter1h',  label: '1h Candle',  tip: 'Require last 1-hour candle to match signal direction.' },
            { key: 'lastCandleFilter4h',  label: '4h Candle',  tip: 'Require last 4-hour candle to match signal direction.' },
          ].map((tf) => (
            <Grid item xs={6} sm={4} md={3} key={tf.key}>
              <FormControlLabel
                control={<Switch size="small" checked={isBoolTrue(settings[tf.key])} onChange={(e) => handleSettingChange(tf.key, e.target.checked)} />}
                label={<Tooltip title={tf.tip} arrow><Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{tf.label}</Typography></Tooltip>}
              />
            </Grid>
          ))}
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolTrue(settings.enableMLGate)} onChange={(e) => handleSettingChange("enableMLGate", e.target.checked)} />}
              label={<Tooltip title="Uses a Bayesian machine learning model to score trade quality and block low-probability setups." arrow><Typography variant="body2">ML Gate (Bayesian)</Typography></Tooltip>}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  // ── Section: Exit System (Custom mode only) ────────────────────────────
  const exitSystemSection = settings.exitStrategy !== "custom" ? null : (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SmartToyIcon color="secondary" fontSize="small" />
            <Tooltip title="Fine-tune stop loss, take profit, trailing stops, and AI exit features. Only available in Custom exit mode." arrow>
              <Typography variant="subtitle2" fontWeight="bold">Exit System (Custom)</Typography>
            </Tooltip>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1.5}>
            {/* SL / TP */}
            <Grid item xs={6}>
              <Tooltip title="Maximum allowed price drop (gross) before the position is closed. Acts as a safety cap — AI may close earlier." arrow>
                <TextField fullWidth size="small" type="number" label="Stop Loss (%)"
                  value={settings.stopLossPercent || "1.5"}
                  onChange={(e) => setSettings({ ...settings, stopLossPercent: e.target.value })}
                />
              </Tooltip>
            </Grid>
            <Grid item xs={6}>
              <Tooltip title="Target profit level (net, after fees). Position closes when net P&L reaches this target." arrow>
                <TextField fullWidth size="small" type="number" label="Take Profit (%)"
                  value={settings.takeProfitPercent || "2.5"}
                  onChange={(e) => setSettings({ ...settings, takeProfitPercent: e.target.value })}
                />
              </Tooltip>
            </Grid>

            {/* Trailing Stop */}
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch size="small" checked={isBoolTrue(settings.useTrailingStop)} onChange={(e) => handleSettingChange("useTrailingStop", e.target.checked ? "Yes" : "No")} />}
                label={<Tooltip title="Dynamically moves the stop loss upward as price increases, locking in profits while letting winners run." arrow><Typography variant="body2">Trailing Stop</Typography></Tooltip>}
              />
            </Grid>
            {isBoolTrue(settings.useTrailingStop) && (
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Switch size="small" checked={isBoolTrue(settings.trailingStopAiMode)} onChange={(e) => handleSettingChange("trailingStopAiMode", e.target.checked)} color="secondary" />}
                  label={<Tooltip title="Let AI dynamically adjust trailing distance based on volatility and market conditions instead of using a fixed percentage." arrow><Typography variant="body2"><SmartToyIcon sx={{ fontSize: "inherit", mr: 0.5 }} />AI Trailing</Typography></Tooltip>}
                />
              </Grid>
            )}
            {isBoolTrue(settings.useTrailingStop) && !isBoolTrue(settings.trailingStopAiMode) && (
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="number" label="Trailing %"
                  value={settings.trailingStopPercent || "0.6"}
                  onChange={(e) => setSettings({ ...settings, trailingStopPercent: e.target.value })}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="caption" sx={{ fontWeight: "bold", color: "info.main" }}>
                AI Exit Feature Toggles
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch size="small" checked={isBoolOn(settings.adaptiveProfitTrailEnabled)} onChange={(e) => handleSettingChange("adaptiveProfitTrailEnabled", e.target.checked)} color="info" />}
                label={<Tooltip title="ATR-based adaptive trailing: automatically adjusts trailing distance based on current market volatility. Tighter in calm markets, wider in volatile ones." arrow><Typography variant="body2">Adaptive Profit Trail</Typography></Tooltip>}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch size="small" checked={isBoolOn(settings.trailingStopFloorEnabled)} onChange={(e) => handleSettingChange("trailingStopFloorEnabled", e.target.checked)} color="info" />}
                label={<Tooltip title="Sets a minimum trailing distance floor to prevent the trailing stop from getting too tight and causing premature exits due to noise." arrow><Typography variant="body2">Trailing Stop Floor</Typography></Tooltip>}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch size="small" checked={isBoolOn(settings.multiTargetTPEnabled)} onChange={(e) => handleSettingChange("multiTargetTPEnabled", e.target.checked)} color="info" />}
                label={<Tooltip title="Takes partial profits at multiple levels (e.g., 1R, 1.5R, 2R). Reduces risk by locking in gains incrementally while letting the remainder run." arrow><Typography variant="body2">Multi-Target TP</Typography></Tooltip>}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch size="small" checked={isBoolOn(settings.zeroLockMeaningfulEnabled)} onChange={(e) => handleSettingChange("zeroLockMeaningfulEnabled", e.target.checked)} color="info" />}
                label={<Tooltip title="Requires the position to sustain profit for multiple ticks before activating zero-lock (break-even protection). Prevents premature locking on brief spikes." arrow><Typography variant="body2">Meaningful Zero-Lock</Typography></Tooltip>}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch size="small" checked={isBoolOn(settings.aiCloseGuardEnabled)} onChange={(e) => handleSettingChange("aiCloseGuardEnabled", e.target.checked)} color="info" />}
                label={<Tooltip title="Prevents AI-driven exits while the position is in net loss and still has recovery potential. Gives trades more room to recover." arrow><Typography variant="body2">AI Close Guard</Typography></Tooltip>}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch size="small" checked={isBoolOn(settings.intentAutoDetectEnabled)} onChange={(e) => handleSettingChange("intentAutoDetectEnabled", e.target.checked)} color="info" />}
                label={<Tooltip title="Automatically classifies each trade as scalp or trend based on market conditions, then adjusts TP targets and trailing behavior accordingly." arrow><Typography variant="body2">Intent Auto-Detect</Typography></Tooltip>}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch size="small" checked={isBoolTrue(settings.fastToGreenTimeoutEnabled)} onChange={(e) => handleSettingChange("fastToGreenTimeoutEnabled", e.target.checked ? "Yes" : "No")} color="warning" />}
                label={<Tooltip title="Closes the position if it doesn't reach profitability within a time limit. Frees up capital from stagnant trades." arrow><Typography variant="body2">Fast-To-Green Timeout</Typography></Tooltip>}
              />
            </Grid>

            {/* Advanced thresholds */}
            <Grid item xs={12}>
              <Accordion sx={{ bgcolor: "background.default" }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="caption" fontWeight="bold">Advanced: Exit Thresholds</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" type="number" label="ATR Multiplier"
                        value={settings.adaptiveProfitTrailATRMultiplier ?? 0.5}
                        onChange={(e) => handleSettingChange("adaptiveProfitTrailATRMultiplier", parseFloat(e.target.value))}
                        inputProps={{ step: 0.1, min: 0.1, max: 2.0 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" type="number" label="Multi-TP Tighten"
                        value={settings.multiTargetTightenAfterR1 ?? 0.7}
                        onChange={(e) => handleSettingChange("multiTargetTightenAfterR1", parseFloat(e.target.value))}
                        inputProps={{ step: 0.05, min: 0.3, max: 1.0 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" type="number" label="Zero-Lock Ticks"
                        value={settings.zeroLockSustainedTicks ?? 3}
                        onChange={(e) => handleSettingChange("zeroLockSustainedTicks", parseInt(e.target.value))}
                        inputProps={{ step: 1, min: 1, max: 10 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" type="number" label="AI Guard Min TP%"
                        value={settings.aiCloseGuardMinTPFraction ?? 0.15}
                        onChange={(e) => handleSettingChange("aiCloseGuardMinTPFraction", parseFloat(e.target.value))}
                        inputProps={{ step: 0.05, min: 0, max: 0.5 }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
  );

  // ── Section: Trailing TP/SL & Zero-Lock ──────────────────────────────
  const rescueActive = isBoolTrue(settings.rescueScaleInEnabled);
  const tslBehavior = settings.tslBehavior || 'DISABLE_TSL_UNTIL_RESCUES_SPENT';
  const trailingZeroLockSection = (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TuneIcon color="info" fontSize="small" />
          <Tooltip title="Configure trailing take-profit/stop-loss and zero-lock (break-even protection). When rescue is active, trailing is suppressed until rescues are spent, then re-enabled automatically." arrow>
            <Typography variant="subtitle2" fontWeight="bold">Trailing TP/SL & Zero-Lock</Typography>
          </Tooltip>
          {isBoolTrue(settings.useTrailingStop) && (
            <Chip
              label={rescueActive && tslBehavior === 'DISABLE_TSL_UNTIL_RESCUES_SPENT' ? "Rescue-Aware" : "Active"}
              size="small"
              color={rescueActive ? "warning" : "info"}
              variant="outlined"
              sx={{ fontSize: "0.65rem", height: 20 }}
            />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1.5}>
          {/* Rescue-aware info banner */}
          {rescueActive && tslBehavior === 'DISABLE_TSL_UNTIL_RESCUES_SPENT' && (
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ py: 0.25 }}>
                <Typography variant="caption">
                  <strong>Rescue-Aware Mode:</strong> Trailing TP/SL is <strong>disabled</strong> while rescue adds remain. Once all rescues are spent (or position recovers), trailing re-enables automatically. Zero-lock activates after rescue recovery.
                </Typography>
              </Alert>
            </Grid>
          )}
          {!rescueActive && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ py: 0.25 }}>
                <Typography variant="caption">
                  Rescue is <strong>off</strong> — trailing TP/SL and zero-lock operate normally from entry.
                </Typography>
              </Alert>
            </Grid>
          )}

          {/* ── Trailing TP/SL ── */}
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="caption" sx={{ fontWeight: "bold", color: "info.main" }}>
              Trailing Take-Profit / Stop-Loss
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolTrue(settings.useTrailingStop)} onChange={(e) => handleSettingChange("useTrailingStop", e.target.checked ? "Yes" : "No")} color="info" />}
              label={<Tooltip title="Dynamically moves the stop upward as profit increases, locking in gains while letting winners run." arrow><Typography variant="body2">Trailing Stop</Typography></Tooltip>}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolTrue(settings.trailingStopAiMode)} onChange={(e) => handleSettingChange("trailingStopAiMode", e.target.checked)} color="secondary" />}
              label={<Tooltip title="Let AI dynamically adjust trailing distance based on volatility and market conditions instead of using a fixed percentage." arrow><Typography variant="body2"><SmartToyIcon sx={{ fontSize: "inherit", mr: 0.5 }} />AI Trailing</Typography></Tooltip>}
            />
          </Grid>

          {isBoolTrue(settings.useTrailingStop) && (
            <>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="number" label="Trailing Distance %"
                  value={settings.trailingStopPercent || "0.30"}
                  onChange={(e) => handleSettingChange("trailingStopPercent", e.target.value)}
                  inputProps={{ step: 0.05, min: 0.05, max: 3.0 }}
                  helperText="Drop from peak to close"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="number" label="Profit Trail Net %"
                  value={settings.profitTrailDistanceNet ?? "0.30"}
                  onChange={(e) => handleSettingChange("profitTrailDistanceNet", parseFloat(e.target.value))}
                  inputProps={{ step: 0.05, min: 0.05, max: 3.0 }}
                  helperText="Net trail distance"
                />
              </Grid>

              <Grid item xs={6}>
                <FormControlLabel
                  control={<Switch size="small" checked={isBoolOn(settings.adaptiveProfitTrailEnabled)} onChange={(e) => handleSettingChange("adaptiveProfitTrailEnabled", e.target.checked)} color="info" />}
                  label={<Tooltip title="ATR-based adaptive trailing: automatically adjusts distance based on current volatility." arrow><Typography variant="body2">Adaptive Trail (ATR)</Typography></Tooltip>}
                />
              </Grid>
              {isBoolOn(settings.adaptiveProfitTrailEnabled) && (
                <Grid item xs={6}>
                  <TextField fullWidth size="small" type="number" label="ATR Multiplier"
                    value={settings.adaptiveProfitTrailATRMultiplier ?? 0.5}
                    onChange={(e) => handleSettingChange("adaptiveProfitTrailATRMultiplier", parseFloat(e.target.value))}
                    inputProps={{ step: 0.1, min: 0.1, max: 2.0 }}
                    helperText="Lower = tighter trail"
                  />
                </Grid>
              )}

              <Grid item xs={6}>
                <FormControlLabel
                  control={<Switch size="small" checked={isBoolOn(settings.trailingStopFloorEnabled)} onChange={(e) => handleSettingChange("trailingStopFloorEnabled", e.target.checked)} color="info" />}
                  label={<Tooltip title="Minimum trailing distance floor — prevents premature exits from noise." arrow><Typography variant="body2">Trail Floor</Typography></Tooltip>}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Switch size="small" checked={isBoolOn(settings.multiTargetTPEnabled)} onChange={(e) => handleSettingChange("multiTargetTPEnabled", e.target.checked)} color="info" />}
                  label={<Tooltip title="Partial TP at multiple levels (1R, 1.5R, 2R) — locks in gains incrementally." arrow><Typography variant="body2">Multi-Target TP</Typography></Tooltip>}
                />
              </Grid>
              {isBoolOn(settings.multiTargetTPEnabled) && (
                <Grid item xs={6}>
                  <TextField fullWidth size="small" type="number" label="Tighten After R1"
                    value={settings.multiTargetTightenAfterR1 ?? 0.7}
                    onChange={(e) => handleSettingChange("multiTargetTightenAfterR1", parseFloat(e.target.value))}
                    inputProps={{ step: 0.05, min: 0.3, max: 1.0 }}
                    helperText="Trail tightens after 1st target"
                  />
                </Grid>
              )}
            </>
          )}

          {/* ── Zero-Lock (Break-Even Protection) ── */}
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="caption" sx={{ fontWeight: "bold", color: "success.main" }}>
              Zero-Lock (Break-Even Protection)
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolOn(settings.zeroLockMeaningfulEnabled)} onChange={(e) => handleSettingChange("zeroLockMeaningfulEnabled", e.target.checked)} color="success" />}
              label={<Tooltip title="Require sustained profit before locking at break-even. Prevents premature locking on brief spikes." arrow><Typography variant="body2">Meaningful Zero-Lock</Typography></Tooltip>}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolOn(settings.zeroLockTrailingEnabled)} onChange={(e) => handleSettingChange("zeroLockTrailingEnabled", e.target.checked)} color="success" />}
              label={<Tooltip title="Zero-lock floor trails upward as profit grows — ratchets up the minimum guaranteed profit." arrow><Typography variant="body2">Trailing Zero-Lock</Typography></Tooltip>}
            />
          </Grid>

          {isBoolOn(settings.zeroLockMeaningfulEnabled) && (
            <>
              <Grid item xs={4}>
                <TextField fullWidth size="small" type="number" label="Sustained Ticks"
                  value={settings.zeroLockSustainedTicks ?? 3}
                  onChange={(e) => handleSettingChange("zeroLockSustainedTicks", parseInt(e.target.value))}
                  inputProps={{ step: 1, min: 1, max: 10 }}
                  helperText="Ticks in profit before lock"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" type="number" label="BE Floor Buffer %"
                  value={settings.beFloorBufferPercent ?? 0.05}
                  onChange={(e) => handleSettingChange("beFloorBufferPercent", parseFloat(e.target.value))}
                  inputProps={{ step: 0.01, min: 0, max: 0.5 }}
                  helperText="Buffer above break-even"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" type="number" label="Slippage Buffer %"
                  value={settings.slippageBufferPercent ?? 0.10}
                  onChange={(e) => handleSettingChange("slippageBufferPercent", parseFloat(e.target.value))}
                  inputProps={{ step: 0.01, min: 0, max: 0.5 }}
                  helperText="Slippage safety margin"
                />
              </Grid>

              {/* v4.7.0: Two-Stage Zero-Lock */}
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="caption" sx={{ fontWeight: "bold", color: "success.main" }}>
                  Two-Stage Zero-Lock (v4.7)
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="number" label="Stage 2 Threshold %"
                  value={settings.zeroLockStage2Threshold ?? 0.30}
                  onChange={(e) => handleSettingChange("zeroLockStage2Threshold", parseFloat(e.target.value))}
                  inputProps={{ step: 0.05, min: 0.10, max: 1.0 }}
                  helperText="Net % to arm profit lock (Stage 2)"
                />
              </Grid>
              {isBoolTrue(settings.rescueScaleInEnabled) && (
                <Grid item xs={6}>
                  <TextField fullWidth size="small" type="number" label="Rescue Timebox (min)"
                    value={settings.rescueWinConversionTimeoutMin ?? 5}
                    onChange={(e) => handleSettingChange("rescueWinConversionTimeoutMin", parseInt(e.target.value))}
                    inputProps={{ step: 1, min: 1, max: 30 }}
                    helperText="Minutes to recover after rescue"
                  />
                </Grid>
              )}
            </>
          )}

          {/* ── TSL Behavior During Rescue ── */}
          {rescueActive && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="caption" sx={{ fontWeight: "bold", color: "warning.main" }}>
                  TSL Behavior During Rescue
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>TSL During Rescue</InputLabel>
                  <Select
                    value={tslBehavior}
                    label="TSL During Rescue"
                    onChange={(e) => handleSettingChange("tslBehavior", e.target.value)}
                  >
                    <MenuItem value="DISABLE_TSL_UNTIL_RESCUES_SPENT">
                      <Box>
                        <Typography variant="body2" fontWeight="bold">Disable Until Rescues Spent</Typography>
                        <Typography variant="caption" color="text.secondary">Recommended — TSL off while rescue adds remain</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="ALLOW_TSL_AFTER_LAST_RESCUE_EXECUTED">
                      <Box>
                        <Typography variant="body2" fontWeight="bold">Enable After Last Rescue</Typography>
                        <Typography variant="caption" color="text.secondary">TSL activates only after final rescue add</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="ALLOW_TSL_ALWAYS">
                      <Box>
                        <Typography variant="body2" fontWeight="bold">Always Allow TSL</Typography>
                        <Typography variant="caption" color="text.secondary">Advanced — TSL active even during rescue (may conflict)</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {/* AI Exit Guards */}
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="caption" sx={{ fontWeight: "bold", color: "secondary.main" }}>
              AI Exit Guards
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolOn(settings.aiCloseGuardEnabled)} onChange={(e) => handleSettingChange("aiCloseGuardEnabled", e.target.checked)} color="secondary" />}
              label={<Tooltip title="Prevents AI exits while position is in net loss and has recovery potential." arrow><Typography variant="body2">AI Close Guard</Typography></Tooltip>}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolOn(settings.intentAutoDetectEnabled)} onChange={(e) => handleSettingChange("intentAutoDetectEnabled", e.target.checked)} color="secondary" />}
              label={<Tooltip title="Auto-classifies each trade as scalp or trend, adjusting TP and trailing accordingly." arrow><Typography variant="body2">Intent Auto-Detect</Typography></Tooltip>}
            />
          </Grid>
          {isBoolOn(settings.aiCloseGuardEnabled) && (
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="AI Guard Min TP%"
                value={settings.aiCloseGuardMinTPFraction ?? 0.15}
                onChange={(e) => handleSettingChange("aiCloseGuardMinTPFraction", parseFloat(e.target.value))}
                inputProps={{ step: 0.05, min: 0, max: 0.5 }}
                helperText="Min TP fraction for AI close"
              />
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  // ── Section: Rescue Scale-In ───────────────────────────────────────────
  const rescueMode = settings.rescueMode || 'AI_ONLY';
  const rescueSection = (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ShieldIcon color="warning" fontSize="small" />
          <Tooltip title="Adds to losing positions to lower average entry and improve recovery chances. AI evaluates conditions before each rescue add." arrow>
            <Typography variant="subtitle2" fontWeight="bold">Rescue Scale-In</Typography>
          </Tooltip>
          <Chip
            label={isBoolTrue(settings.rescueScaleInEnabled) ? `ON · ${rescueMode === 'MANUAL_PLUS_AI' ? 'Manual+AI' : 'AI Only'}` : "OFF"}
            size="small"
            color={isBoolTrue(settings.rescueScaleInEnabled) ? "warning" : "default"}
            variant="outlined"
            sx={{ fontSize: "0.65rem", height: 20 }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolTrue(settings.rescueScaleInEnabled)} onChange={(e) => handleSettingChange("rescueScaleInEnabled", e.target.checked)} color="warning" />}
              label={<Typography variant="body2">Enable Rescue (add to losing positions)</Typography>}
            />
          </Grid>
          {isBoolTrue(settings.rescueScaleInEnabled) && (
            <>
              {/* Rescue Mode */}
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Rescue Mode</InputLabel>
                  <Select
                    value={rescueMode}
                    label="Rescue Mode"
                    onChange={(e) => handleSettingChange("rescueMode", e.target.value)}
                  >
                    <MenuItem value="AI_ONLY">AI Only — AI decides timing & sizing</MenuItem>
                    <MenuItem value="MANUAL_PLUS_AI">Manual + AI — Threshold triggers, AI decides action</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Manual Trigger Threshold (only for MANUAL_PLUS_AI) */}
              {rescueMode === 'MANUAL_PLUS_AI' && (
                <Grid item xs={12}>
                  <Typography variant="body2" gutterBottom>
                    Trigger Threshold: -{settings.manualTriggerThresholdPct || 3}% net PnL
                  </Typography>
                  <Slider
                    value={parseFloat(settings.manualTriggerThresholdPct) || 3}
                    onChange={(e, value) => handleSettingChange("manualTriggerThresholdPct", value)}
                    min={1} max={10} step={0.5}
                    marks={[{ value: 1, label: '1%' }, { value: 5, label: '5%' }, { value: 10, label: '10%' }]}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `-${v}%`}
                    size="small"
                    color="warning"
                  />
                  <Typography variant="caption" color="text.secondary">
                    When net PnL drops below this threshold, AI evaluates: ADD / HOLD / CLOSE
                  </Typography>
                </Grid>
              )}

              <Divider sx={{ my: 1, width: '100%' }} />

              {/* Max Rescue Attempts */}
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="number" label="Max Rescue Attempts"
                  value={settings.maxRescueAttempts || "3"}
                  onChange={(e) => handleSettingChange("maxRescueAttempts", e.target.value)}
                  inputProps={{ min: 1, max: 10 }}
                  helperText="Hard cap per position"
                />
              </Grid>

              {/* Max Rescue Adds (legacy, kept for budget allocation) */}
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="number" label="Rescue Budget Levels"
                  value={settings.maxRescueAdds || "1"}
                  onChange={(e) => handleSettingChange("maxRescueAdds", e.target.value)}
                  inputProps={{ min: 1, max: 4 }}
                  helperText="Budget split levels"
                />
              </Grid>

              <Divider sx={{ my: 1, width: '100%' }} />

              {/* Rescue Add Premium */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2">
                    Rescue Add Size Premium: {isBoolTrue(settings.rescueAddPremiumAI) ? 'AI decides' : `${settings.rescueAddPremiumPct || 10}%`}
                  </Typography>
                  <Tooltip title="When ON, AI chooses premium but add must remain bigger than entry">
                    <FormControlLabel
                      control={<Switch size="small" checked={isBoolTrue(settings.rescueAddPremiumAI)} onChange={(e) => handleSettingChange("rescueAddPremiumAI", e.target.checked)} color="info" />}
                      label={<Typography variant="caption">AI Premium</Typography>}
                      sx={{ mr: 0 }}
                    />
                  </Tooltip>
                </Box>
                {!isBoolTrue(settings.rescueAddPremiumAI) && (
                  <Slider
                    value={parseFloat(settings.rescueAddPremiumPct) || 10}
                    onChange={(e, value) => handleSettingChange("rescueAddPremiumPct", value)}
                    min={5} max={20} step={1}
                    marks={[{ value: 5, label: '5%' }, { value: 10, label: '10%' }, { value: 20, label: '20%' }]}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `+${v}%`}
                    size="small"
                    color="warning"
                  />
                )}
                <Typography variant="caption" color="text.secondary">
                  Rescue add must be at least entry size × (1 + premium). Ensures averaging down is meaningful.
                </Typography>
              </Grid>

              <Divider sx={{ my: 1, width: '100%' }} />

              {/* Rescue Reserve % */}
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  Rescue Reserve: {settings.rescueReservePercent || 5}% of budget
                </Typography>
                <Slider
                  value={parseFloat(settings.rescueReservePercent) || 5}
                  onChange={(e, value) => handleSettingChange("rescueReservePercent", value)}
                  min={0} max={30} step={1}
                  marks={[{ value: 0, label: '0%' }, { value: 10, label: '10%' }, { value: 20, label: '20%' }, { value: 30, label: '30%' }]}
                  valueLabelDisplay="auto"
                  size="small"
                  color="warning"
                />
              </Grid>

              {rescueMode === 'MANUAL_PLUS_AI' && (
                <Grid item xs={12}>
                  <Alert severity="info" variant="outlined" sx={{ fontSize: '0.75rem', py: 0.5 }}>
                    <strong>Manual + AI mode:</strong> When net PnL hits -{settings.manualTriggerThresholdPct || 3}%, 
                    AI evaluates conditions and decides to ADD (rescue), HOLD (wait), or CLOSE (cut loss). 
                    Rescue add will be ≥ {isBoolTrue(settings.rescueAddPremiumAI) ? '(AI-determined premium)' : `${100 + (parseFloat(settings.rescueAddPremiumPct) || 10)}%`} of initial entry size.
                  </Alert>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  // ── Section: Execution Tuning ──────────────────────────────────────────
  const executionSection = (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SpeedIcon color="primary" fontSize="small" />
          <Tooltip title="Control how orders are executed (market vs limit), cooldown between trades, and impulse gate settings." arrow>
            <Typography variant="subtitle2" fontWeight="bold">Execution & Timing</Typography>
          </Tooltip>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Manage Interval</InputLabel>
              <Select value={settings.manageInterval || "1m"} onChange={(e) => handleSettingChange("manageInterval", e.target.value)} label="Manage Interval">
                <MenuItem value="1m">1 min</MenuItem>
                <MenuItem value="2m">2 min</MenuItem>
                <MenuItem value="5m">5 min</MenuItem>
                <MenuItem value="10m">10 min</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Execution Mode</InputLabel>
              <Select value={settings.executionMode || "limit_first"} onChange={(e) => handleSettingChange("executionMode", e.target.value)} label="Execution Mode">
                <MenuItem value="market">Market</MenuItem>
                <MenuItem value="limit_first">Limit-First</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Cooldown */}
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="number" label="Cooldown (min)"
              value={settings.cooldownMinutes || "3"}
              onChange={(e) => handleSettingChange("cooldownMinutes", e.target.value)}
              helperText="Between trades on same symbol"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolTrue(settings.cooldownPerSymbol)} onChange={(e) => handleSettingChange("cooldownPerSymbol", e.target.checked ? "Yes" : "No")} />}
              label={<Typography variant="body2">Per-Symbol Cooldown</Typography>}
            />
          </Grid>

          {/* Impulse Gate */}
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolOn(settings.impulseGateEnabled)} onChange={(e) => handleSettingChange("impulseGateEnabled", e.target.checked)} />}
              label={<Typography variant="body2">Impulse Gate</Typography>}
            />
          </Grid>
          {isBoolOn(settings.impulseGateEnabled) && (
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Fail Action</InputLabel>
                <Select value={settings.impulseGateFailAction || "PENDING"} onChange={(e) => handleSettingChange("impulseGateFailAction", e.target.value)} label="Fail Action">
                  <MenuItem value="PENDING">Defer to Pending</MenuItem>
                  <MenuItem value="REJECT">Reject</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Limit-First advanced */}
          {(settings.executionMode || "limit_first") === "limit_first" && (
            <>
              <Grid item xs={12}>
                <Accordion sx={{ bgcolor: "background.default" }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="caption" fontWeight="bold">Advanced: Limit-First Settings</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <TextField fullWidth size="small" type="number" label="Max Entry Slip %"
                          value={settings.limitFirstMaxEntrySlipPct ?? 0.08}
                          onChange={(e) => handleSettingChange("limitFirstMaxEntrySlipPct", parseFloat(e.target.value))}
                          inputProps={{ step: 0.01, min: 0.01, max: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth size="small" type="number" label="Timeout (ms)"
                          value={settings.limitFirstTimeoutMs ?? 600}
                          onChange={(e) => handleSettingChange("limitFirstTimeoutMs", parseInt(e.target.value))}
                          inputProps={{ step: 100, min: 100, max: 5000 }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={<Switch size="small" checked={isBoolOn(settings.limitFirstAllowMarketFallback)} onChange={(e) => handleSettingChange("limitFirstAllowMarketFallback", e.target.checked)} />}
                          label={<Typography variant="body2">Market Fallback</Typography>}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  // ── Section: Trading Hours ─────────────────────────────────────────────
  const tradingHoursSection = (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TuneIcon color="primary" fontSize="small" />
          <Tooltip title="Restrict trading to specific hours or use Golden Hours mode to focus on optimal trading windows per day." arrow>
            <Typography variant="subtitle2" fontWeight="bold">Trading Hours & Schedule</Typography>
          </Tooltip>
          <Chip
            label={isBoolTrue(settings.tradingHoursEnabled) ? "Restricted" : isBoolTrue(settings.goldenHoursMode) ? "Golden" : "24/7"}
            size="small"
            color={isBoolTrue(settings.tradingHoursEnabled) ? "warning" : "default"}
            variant="outlined"
            sx={{ fontSize: "0.65rem", height: 20 }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolTrue(settings.tradingHoursEnabled)} onChange={(e) => handleSettingChange("tradingHoursEnabled", e.target.checked)} />}
              label={
                <Typography variant="body2">
                  {isBoolTrue(settings.tradingHoursEnabled) ? "Restricted Hours" : "24/7 Trading"}
                </Typography>
              }
            />
          </Grid>
          {isBoolTrue(settings.tradingHoursEnabled) && (
            <>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="time" label="Start"
                  value={settings.tradingHoursStart || "00:00"}
                  onChange={(e) => handleSettingChange("tradingHoursStart", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="time" label="End"
                  value={settings.tradingHoursEnd || "23:59"}
                  onChange={(e) => handleSettingChange("tradingHoursEnd", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Times in your local timezone ({settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}). Converted to UTC on save.
                </Typography>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
          </Grid>

          {/* Golden Hours - Market Sessions Integration */}
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolTrue(settings.goldenHoursMode)} onChange={(e) => handleSettingChange("goldenHoursMode", e.target.checked ? "Yes" : "No")} color="warning" />}
              label={<Typography variant="body2">🌟 Golden Hours Mode</Typography>}
            />
          </Grid>
          {isBoolTrue(settings.goldenHoursMode) && (
            <>
              {/* Market Sessions Selection */}
              <Grid item xs={12}>
                <Box sx={{ p: 1.5, bgcolor: 'rgba(245, 158, 11, 0.1)', borderRadius: 1, border: '1px solid rgba(245, 158, 11, 0.3)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PublicIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                    <Typography variant="body2" fontWeight="bold">Trade During Market Sessions</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Select which market sessions to trade during. Trading will only occur when selected markets are open (weekdays only).
                  </Typography>
                  <FormGroup row>
                    {MARKET_SESSIONS.map((market) => (
                      <FormControlLabel
                        key={market.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={settings.marketSessions?.[market.id] !== false}
                            onChange={(e) => {
                              const newMarketSessions = { ...(settings.marketSessions || {}) };
                              newMarketSessions[market.id] = e.target.checked;
                              handleSettingChange("marketSessions", newMarketSessions);
                            }}
                            sx={{ color: market.color, '&.Mui-checked': { color: market.color } }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>{market.flag}</span>
                            <Typography variant="caption">{market.name}</Typography>
                          </Box>
                        }
                      />
                    ))}
                  </FormGroup>
                  <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                    <Typography variant="caption">
                      <strong>Weekend:</strong> All markets closed. <strong>Golden Hours:</strong> First 3 hours of each session (highest volume).
                    </Typography>
                  </Alert>
                </Box>
              </Grid>

              {/* Golden Hours Only Toggle */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={isBoolTrue(settings.goldenHoursOnly)}
                      onChange={(e) => handleSettingChange("goldenHoursOnly", e.target.checked)}
                      color="warning"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      🌟 Trade Only During Golden Hours (first 3 hours of each session)
                    </Typography>
                  }
                />
              </Grid>

              {/* Weekend Trading Toggle */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={isBoolTrue(settings.allowWeekendTrading)}
                      onChange={(e) => handleSettingChange("allowWeekendTrading", e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Allow Weekend Trading (Crypto only)</Typography>}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="caption" color="text.secondary">
                  Or set custom times per day:
                </Typography>
              </Grid>

              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <Grid item xs={6} sm={3} key={day}>
                  <TextField
                    fullWidth size="small" type="time"
                    label={day}
                    value={settings[`goldenHour${day}`] || ""}
                    onChange={(e) => handleSettingChange(`goldenHour${day}`, e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    placeholder="--:--"
                    disabled={day === "Sat" || day === "Sun" ? !isBoolTrue(settings.allowWeekendTrading) : false}
                  />
                </Grid>
              ))}
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Repeat Interval</InputLabel>
                  <Select value={settings.goldenRepeatInterval || "5m"} onChange={(e) => handleSettingChange("goldenRepeatInterval", e.target.value)} label="Repeat Interval">
                    <MenuItem value="1m">1 min</MenuItem>
                    <MenuItem value="5m">5 min</MenuItem>
                    <MenuItem value="10m">10 min</MenuItem>
                    <MenuItem value="15m">15 min</MenuItem>
                    <MenuItem value="30m">30 min</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="number" label="Repeat Count"
                  value={settings.goldenRepeatCount || "10"}
                  onChange={(e) => handleSettingChange("goldenRepeatCount", e.target.value)}
                  inputProps={{ min: 1, max: 50 }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  // ── Section: Retry & Notifications ─────────────────────────────────────
  const retryNotificationsSection = (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Tooltip title="Configure automatic retry on failed trades and notification preferences for trade events and errors." arrow>
          <Typography variant="subtitle2" fontWeight="bold">Retry & Notifications</Typography>
        </Tooltip>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1.5}>
          <Grid item xs={4}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolTrue(settings.retryOnFailure)} onChange={(e) => handleSettingChange("retryOnFailure", e.target.checked ? "Yes" : "No")} />}
              label={<Typography variant="body2">Retry</Typography>}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" type="number" label="Max Retries"
              value={settings.maxRetries || "3"}
              onChange={(e) => handleSettingChange("maxRetries", e.target.value)}
              disabled={!isBoolTrue(settings.retryOnFailure)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" type="number" label="Delay (sec)"
              value={settings.retryDelaySeconds || "60"}
              onChange={(e) => handleSettingChange("retryDelaySeconds", e.target.value)}
              disabled={!isBoolTrue(settings.retryOnFailure)}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolTrue(settings.notifyOnTrade)} onChange={(e) => handleSettingChange("notifyOnTrade", e.target.checked ? "Yes" : "No")} />}
              label={<Typography variant="body2">On Trade</Typography>}
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolTrue(settings.notifyOnError)} onChange={(e) => handleSettingChange("notifyOnError", e.target.checked ? "Yes" : "No")} />}
              label={<Typography variant="body2">On Error</Typography>}
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={<Switch size="small" checked={isBoolTrue(settings.telegramEnabled)} onChange={(e) => handleSettingChange("telegramEnabled", e.target.checked ? "Yes" : "No")} />}
              label={<Typography variant="body2">Telegram</Typography>}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
        Advanced Controls
      </Typography>
      {decisionModelSection}
      {moneyManagementSection}
      {entryFiltersSection}
      {exitSystemSection}

      {/* Fixed TP Ceiling — always visible regardless of exit mode (v4.0.15) */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MonetizationOnIcon color="warning" fontSize="small" />
            <Tooltip title="Hard profit ceiling that instantly closes a position when net P&L reaches the target. Catches quick price spikes that trailing TP might miss. Works alongside all exit modes." arrow>
              <Typography variant="subtitle2" fontWeight="bold">Fixed TP Ceiling</Typography>
            </Tooltip>
            {isBoolTrue(settings.fixedTpEnabled) && (
              <Chip
                label={`${settings.fixedTpPercent || 0}%`}
                size="small"
                color="success"
                sx={{ fontSize: "0.65rem", height: 20 }}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch size="small" checked={isBoolTrue(settings.fixedTpEnabled)} onChange={(e) => handleSettingChange("fixedTpEnabled", e.target.checked)} color="success" />}
                label={<Typography variant="body2">Enable Fixed TP</Typography>}
              />
            </Grid>
            {isBoolTrue(settings.fixedTpEnabled) && (
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="number" label="Fixed TP %"
                  value={settings.fixedTpPercent || ""}
                  onChange={(e) => handleSettingChange("fixedTpPercent", e.target.value)}
                  inputProps={{ step: 0.5, min: 0.5, max: 50 }}
                  helperText="Hard close at this net P&L%"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Alert severity="info" sx={{ py: 0.25 }}>
                <Typography variant="caption">
                  Instantly closes when net P&L reaches the ceiling. Set higher than your trailing TP target to only catch extreme spikes.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Signal Matrix — Indicator Stack (v4.4.0) */}
      <IndicatorStackPanel settings={settings} setSettings={setSettings} />

      {trailingZeroLockSection}
      {rescueSection}
      {executionSection}
      {tradingHoursSection}
      {retryNotificationsSection}
    </Box>
  );
}
